<template>
    <aside
        v-if="season && !isDismissed"
        class="season_greeting fixed z-[990] bottom-3 inset-x-3 sm:inset-x-auto sm:bottom-6 sm:end-6 sm:w-[360px]"
        :aria-label="title"
    >
        <div class="relative flex items-start gap-3 p-4 text-white season_card">
            <!-- corners -->
            <span class="absolute top-0 left-0 border-t-[1.5px] border-l-[1.5px] border-primary size-[10px]" aria-hidden="true"></span>
            <span class="absolute bottom-0 right-0 border-b-[1.5px] border-r-[1.5px] border-primary size-[10px]" aria-hidden="true"></span>

            <button
                type="button"
                class="grid border rounded-full shrink-0 size-10 place-content-center border-primary-light text-primary season_replay"
                :aria-label="$t('season.replay')"
                @click="replay"
            >
                <!-- crescent and star for Hijri, a snowflake for the (cold) Gregorian year -->
                <svg v-if="season.name === 'hijri'" class="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M15.5 4.5a8 8 0 1 0 4 12 6.5 6.5 0 0 1-4-12Z" />
                    <path d="m18.5 6 .6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6Z" />
                </svg>
                <svg v-else class="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M2 12h20M12 2v20" />
                    <path d="m20 16-4-4 4-4M4 8l4 4-4 4M16 4l-4 4-4-4M8 20l4-4 4 4" />
                </svg>
            </button>

            <div class="flex-1 min-w-0">
                <p class="text-base leading-tight font-pp custom_title">{{ title }}</p>
                <p class="mt-1 text-sm font-gt-light text-white/70">{{ $t(`season.${season.name}.message`) }}</p>
            </div>

            <button
                type="button"
                class="grid transition shrink-0 size-8 place-content-center text-primary-light hover:text-primary"
                :aria-label="$t('season.dismiss')"
                @click="dismiss"
            >
                <AppIcon name="multiplication-sign" class="text-lg leading-none" />
            </button>
        </div>
    </aside>
</template>

<script setup>
const season = useSeason()
const { t } = useI18n()

// Remembered per occasion and year, in a cookie so the server does not render a dismissed card.
const dismissed = useCookie('season_dismissed', { maxAge: 60 * 60 * 24 * 30, sameSite: 'lax' })
const seasonKey = computed(() => season.value && `${season.value.name}-${season.value.year}`)
const isDismissed = computed(() => dismissed.value === seasonKey.value)

const title = computed(() => season.value && t(`season.${season.value.name}.title`, { year: season.value.year }))

// SeasonEffect watches this counter and plays again.
const replayCount = useState('seasonReplay', () => 0)
const replay = () => replayCount.value++

const dismiss = () => {
    dismissed.value = seasonKey.value
}
</script>

<style scoped>
.season_card {
    background: rgba(20, 20, 28, 0.92);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid var(--color-primary-light);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5), 0 0 30px rgb(var(--primary-rgb) / 0.08);
}

.season_greeting {
    animation: season_in 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) 0.8s both;
}
@keyframes season_in {
    from {
        opacity: 0;
        transform: translateY(16px);
    }
}

.season_replay {
    transition: border-color 0.2s ease, transform 0.3s ease;
}
.season_replay:hover {
    border-color: var(--color-primary);
    transform: rotate(20deg);
}

@media (prefers-reduced-motion: reduce) {
    .season_greeting {
        animation: none;
    }
}
</style>
